/**
 * New Year menus — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearMenusPack: ToilePack = {
    slug: 'new-year-menus',
    category: 'season',
    name: 'New Year menus',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dinner', label: 'Dinner menu', subject: 'A vertical menu card lettered "NEW YEAR\'S EVE" with headings "Starter", "Main" and "Dessert" and blank lines, a gold border'},
        {key: 'cocktails', label: 'Cocktail list', subject: 'A vertical card lettered "COCKTAILS" with a long blank ruled list and a coupe glass motif'},
        {key: 'brunch', label: "New Year's Day brunch", subject: 'A vertical menu card lettered "NEW YEAR\'S DAY BRUNCH" with blank lines and a sunrise motif'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['a vegetarian menu version instead', 'for a champagne bar only', 'as a small tented tabletop card'],
    directionHint: 'try: a vegetarian menu instead',
    seo: {
        title: 'New Year Menus: Dinner, Cocktails & Brunch',
        metaDescription: "Nine New Year menu cards — a dinner menu, a cocktail list, and a New Year's Day brunch menu — courses lettered, dishes left blank to fill in.",
        h1: 'New Year menus: dinner, cocktail list, and brunch',
        tagline: 'Nine cards, three menu types, three styles each — course headers lettered, lines left blank.',
        intro: "Three New Year menu cards — a dinner menu with starter, main, and dessert headers, a cocktail list, and a New Year's Day brunch menu — each with course headers lettered and blank lines for the dishes, in black-and-gold, confetti-party, and minimal styles. Ask for a different course structure, a wine-pairing line, or a buffet layout, and the same menu grammar carries it.",
        useCases: [
            "a New Year's Eve dinner table",
            'a home cocktail bar menu',
            "a New Year's Day brunch spread",
            'a dinner-party place setting',
        ],
        faq: [
            {q: 'Are the dishes already listed?', a: "No — the course headers print lettered ('Starter', 'Main', 'Dessert'), and the lines beneath stay blank for your own menu."},
            {q: 'What size do these print at?', a: 'Each card is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different course structure?', a: 'Yes — the direction field takes any variation, including a different course layout.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
